// Cache and request management
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

export interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

export class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, PendingRequest<any>>();
  
  // Default TTL values (in milliseconds)
  private readonly TTL = {
    HOME_DATA: 5 * 60 * 1000,      // 5 minutes
    USER_DATA: 2 * 60 * 1000,      // 2 minutes
    CART_DATA: 30 * 1000,          // 30 seconds
    COUNTRIES: 30 * 60 * 1000,     // 30 minutes
    CURRENCIES: 30 * 60 * 1000,    // 30 minutes
    CURRENT_COUNTRY: 5 * 60 * 1000, // 5 minutes
    SEARCH: 2 * 60 * 1000,         // 2 minutes
  };

  // Check if cache entry is valid
  private isValid<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  // Get cached data if valid
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (entry && this.isValid(entry)) {
      return entry.data;
    }
    if (entry) {
      this.cache.delete(key); // Remove expired entry
    }
    return null;
  }

  // Set cache entry
  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.TTL.HOME_DATA
    });
  }

  // Check if request is already pending
  getPendingRequest<T>(key: string): Promise<T> | null {
    const pending = this.pendingRequests.get(key);
    if (pending && Date.now() - pending.timestamp < 30000) { // 30 second timeout for pending requests
      return pending.promise;
    }
    if (pending) {
      this.pendingRequests.delete(key); // Remove stale pending request
    }
    return null;
  }

  // Set pending request
  setPendingRequest<T>(key: string, promise: Promise<T>): void {
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    });
  }

  // Clear pending request
  clearPendingRequest(key: string): void {
    this.pendingRequests.delete(key);
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  // Clear cache by pattern
  clearByPattern(pattern: string): void {
    const regex = new RegExp(pattern);
    const cacheKeys = Array.from(this.cache.keys());
    const pendingKeys = Array.from(this.pendingRequests.keys());
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Clearing cache with pattern: ${pattern}`);
      console.log(`Available cache keys:`, cacheKeys);
      console.log(`Available pending keys:`, pendingKeys);
    }
    
    let clearedCount = 0;
    cacheKeys.forEach(key => {
      if (regex.test(key)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Clearing cache key: ${key}`);
        }
        this.cache.delete(key);
        clearedCount++;
      }
    });
    
    pendingKeys.forEach(key => {
      if (regex.test(key)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`Clearing pending key: ${key}`);
        }
        this.pendingRequests.delete(key);
        clearedCount++;
      }
    });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Total keys cleared: ${clearedCount}`);
    }
  }

  // Clear user-specific cache
  clearUserCache(): void {
    this.clearByPattern('^(user|cart|auth)');
  }

  // Clear country-related cache
  clearCountryCache(): void {
    this.clearByPattern('^(home_data|country|currency|search|events|event_)');
  }
}

// Create cache instance
export const apiCache = new ApiCache();

